import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Loader2, CheckCircle2, X } from "lucide-react";
import type { FormSchema, FormPage, FormField, ConditionalRule } from "@shared/form-builder-types";

// ── Analytics helpers ──────────────────────────────────────────────────────
let _analyticsSessionId: string | null = null;
function getSessionId(): string {
  if (!_analyticsSessionId) {
    _analyticsSessionId = Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
  return _analyticsSessionId;
}

async function trackEvent(
  formId: string,
  eventType: "view" | "page_advance" | "field_focus" | "submit" | "abandon",
  pageIndex?: number,
  fieldId?: string
) {
  try {
    await fetch("/api/public/form-analytics", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        formId,
        sessionId: getSessionId(),
        eventType,
        pageIndex,
        fieldId,
      }),
    });
  } catch {
    // best-effort, never block the form
  }
}

// ── Conditional logic evaluator ────────────────────────────────────────────
function evaluateConditions(
  page: FormPage,
  answers: Record<string, any>
): number | null {
  for (const rule of page.conditionalRules ?? []) {
    const answer = answers[rule.fieldId];
    const val = String(answer ?? "").trim();
    const ruleVal = rule.value ?? "";

    let matches = false;
    switch (rule.operator) {
      case "equals":
        matches = val === ruleVal;
        break;
      case "not_equals":
        matches = val !== ruleVal;
        break;
      case "contains":
        matches = val.includes(ruleVal);
        break;
      case "is_before":
        matches = new Date(val) < new Date(ruleVal);
        break;
      case "is_after":
        matches = new Date(val) > new Date(ruleVal);
        break;
      case "is_empty":
        matches = !val;
        break;
      case "is_not_empty":
        matches = !!val;
        break;
    }
    if (matches) return rule.targetPageIndex;
  }
  return null;
}

// ── Field Renderer ─────────────────────────────────────────────────────────
function FieldRenderer({
  field,
  value,
  onChange,
  formId,
}: {
  field: FormField;
  value: any;
  onChange: (v: any) => void;
  formId: string;
}) {
  const handleFocus = () => {
    trackEvent(formId, "field_focus", undefined, field.id);
  };

  switch (field.kind) {
    case "text":
    case "email":
    case "phone":
      return (
        <div className="space-y-1">
          <Label htmlFor={field.id} className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          <Input
            id={field.id}
            type={field.kind === "email" ? "email" : field.kind === "phone" ? "tel" : "text"}
            placeholder={field.placeholder}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            onFocus={handleFocus}
            required={field.required}
            data-testid={`field-${field.id}`}
          />
          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
        </div>
      );

    case "number":
      return (
        <div className="space-y-1">
          <Label htmlFor={field.id} className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          <Input
            id={field.id}
            type="number"
            placeholder={field.placeholder}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            onFocus={handleFocus}
            data-testid={`field-${field.id}`}
          />
          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
        </div>
      );

    case "textarea":
      return (
        <div className="space-y-1">
          <Label htmlFor={field.id} className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          <Textarea
            id={field.id}
            placeholder={field.placeholder}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            onFocus={handleFocus}
            rows={3}
            data-testid={`field-${field.id}`}
          />
          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
        </div>
      );

    case "dropdown":
      return (
        <div className="space-y-1">
          <Label className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          <Select value={value ?? ""} onValueChange={onChange}>
            <SelectTrigger data-testid={`field-${field.id}`}>
              <SelectValue placeholder={field.placeholder ?? "Select…"} />
            </SelectTrigger>
            <SelectContent>
              {(field.options ?? []).map((opt) => (
                <SelectItem key={opt.id} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
              {field.allowOther && <SelectItem value="__other__">Other</SelectItem>}
            </SelectContent>
          </Select>
          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
        </div>
      );

    case "radio":
      return (
        <div className="space-y-1">
          <Label className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          <RadioGroup value={value ?? ""} onValueChange={onChange} data-testid={`field-${field.id}`}>
            {(field.options ?? []).map((opt) => (
              <div key={opt.id} className="flex items-center gap-2">
                <RadioGroupItem value={opt.value} id={`${field.id}-${opt.id}`} />
                <Label htmlFor={`${field.id}-${opt.id}`} className="cursor-pointer font-normal">
                  {opt.label}
                </Label>
              </div>
            ))}
            {field.allowOther && (
              <div className="flex items-center gap-2">
                <RadioGroupItem value="__other__" id={`${field.id}-other`} />
                <Label htmlFor={`${field.id}-other`} className="cursor-pointer font-normal">Other</Label>
              </div>
            )}
          </RadioGroup>
          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
        </div>
      );

    case "checkbox":
    case "multi_select": {
      const checked: string[] = Array.isArray(value) ? value : [];
      const toggle = (v: string) =>
        onChange(checked.includes(v) ? checked.filter((c) => c !== v) : [...checked, v]);
      return (
        <div className="space-y-1">
          <Label className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          <div className="space-y-1.5" data-testid={`field-${field.id}`}>
            {(field.options ?? []).map((opt) => (
              <label key={opt.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={checked.includes(opt.value)}
                  onCheckedChange={() => toggle(opt.value)}
                />
                {opt.label}
              </label>
            ))}
            {field.allowOther && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={checked.includes("__other__")}
                  onCheckedChange={() => toggle("__other__")}
                />
                Other
              </label>
            )}
          </div>
          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
        </div>
      );
    }

    case "date_picker": {
      const dateVal = value ? new Date(value) : undefined;
      return (
        <div className="space-y-1">
          <Label className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          <div className="border rounded-md inline-block" data-testid={`field-${field.id}`}>
            <Calendar
              mode="single"
              selected={dateVal}
              onSelect={(d) => onChange(d?.toISOString())}
              disabled={(d) => {
                const now = new Date();
                const min = field.minAdvanceDays
                  ? new Date(now.getTime() + field.minAdvanceDays * 86400000)
                  : now;
                const max = field.maxAdvanceDays
                  ? new Date(now.getTime() + field.maxAdvanceDays * 86400000)
                  : undefined;
                return d < min || (max ? d > max : false);
              }}
            />
          </div>
          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
        </div>
      );
    }

    case "heading":
      return (
        <div data-testid={`field-${field.id}`}>
          {field.headingLevel === 1 && <h1 className="text-2xl font-bold">{field.label}</h1>}
          {field.headingLevel === 3 && <h3 className="text-lg font-semibold">{field.label}</h3>}
          {(!field.headingLevel || field.headingLevel === 2) && (
            <h2 className="text-xl font-semibold">{field.label}</h2>
          )}
        </div>
      );

    case "divider":
      return <hr className="border-border my-2" data-testid={`field-${field.id}`} />;

    case "image":
      return field.imageUrl ? (
        <img
          src={field.imageUrl}
          alt={field.label}
          className="max-w-full rounded-md"
          data-testid={`field-${field.id}`}
        />
      ) : null;

    case "rich_text":
      return field.richTextHtml ? (
        <div
          className="prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: field.richTextHtml }}
          data-testid={`field-${field.id}`}
        />
      ) : null;

    case "rating": {
      const max = field.ratingMax ?? 5;
      const current = Number(value ?? 0);
      return (
        <div className="space-y-1">
          <Label className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          <div className="flex gap-1" data-testid={`field-${field.id}`}>
            {Array.from({ length: max }, (_, i) => (
              <button
                key={i}
                type="button"
                className={`text-2xl transition-colors ${
                  i < current ? "text-yellow-400" : "text-muted-foreground"
                }`}
                onClick={() => onChange(i + 1)}
              >
                ★
              </button>
            ))}
          </div>
          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
        </div>
      );
    }

    case "event_offerings_picker":
      return (
        <div className="space-y-1">
          <Label className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          <p className="text-xs text-muted-foreground italic">
            (Event offerings will be loaded from your talent configuration)
          </p>
        </div>
      );

    case "availability_calendar":
      return (
        <div className="space-y-1">
          <Label className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          <p className="text-xs text-muted-foreground italic">
            (Availability calendar will be loaded from your{" "}
            {field.availabilityMode === "connected_calendar"
              ? "connected Google Calendar"
              : "master schedule"}
            )
          </p>
        </div>
      );

    case "file_upload":
      return (
        <div className="space-y-1">
          <Label className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          <input
            type="file"
            className="block text-sm"
            onChange={(e) => onChange(e.target.files?.[0]?.name)}
            onFocus={handleFocus}
            data-testid={`field-${field.id}`}
          />
          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
        </div>
      );

    case "signature":
      return (
        <div className="space-y-1">
          <Label className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          <div
            className="border-2 border-dashed rounded-md h-20 flex items-center justify-center text-sm text-muted-foreground"
            data-testid={`field-${field.id}`}
          >
            Signature field
          </div>
          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
        </div>
      );

    case "calendar_booking_slot":
      return (
        <div className="space-y-1">
          <Label className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          <p className="text-xs text-muted-foreground italic">
            (Time slot picker will be shown here)
          </p>
          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
        </div>
      );

    default:
      return null;
  }
}

// ── SchemaFormRenderer ─────────────────────────────────────────────────────
interface SchemaFormRendererProps {
  schema: FormSchema;
  formId: string;
  onSubmit: (answers: Record<string, any>) => void;
  isSubmitting?: boolean;
}

const STORAGE_KEY_PREFIX = "clarik_form_";

export function SchemaFormRenderer({
  schema,
  formId,
  onSubmit,
  isSubmitting = false,
}: SchemaFormRendererProps) {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [showResumeBanner, setShowResumeBanner] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const storageKey = `${STORAGE_KEY_PREFIX}${formId}`;

  // On mount: check localStorage for partial answers, fire "view" event
  useEffect(() => {
    trackEvent(formId, "view", 0);
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === "object" && Object.keys(parsed).length > 0) {
          setShowResumeBanner(true);
        }
      }
    } catch {}
  }, [formId]);

  // Persist answers on every page advance
  const persistAnswers = (a: Record<string, any>) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(a));
    } catch {}
  };

  // Abandon beacon
  useEffect(() => {
    const onUnload = () => {
      if (!submitted && Object.keys(answers).length > 0) {
        navigator.sendBeacon(
          "/api/public/form-analytics",
          JSON.stringify({
            formId,
            sessionId: getSessionId(),
            eventType: "abandon",
            pageIndex: currentPageIndex,
          })
        );
      }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [submitted, answers, currentPageIndex, formId]);

  const resumeAnswers = () => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        setAnswers(parsed);
      }
    } catch {}
    setShowResumeBanner(false);
  };

  const page = schema.pages[currentPageIndex];
  const isLastPage = currentPageIndex >= schema.pages.length - 1;

  const handleNext = () => {
    // Evaluate conditional rules
    const targetIdx = evaluateConditions(page, answers);
    const nextIdx = targetIdx ?? (page.defaultNextPageIndex ?? currentPageIndex + 1);

    persistAnswers(answers);
    trackEvent(formId, "page_advance", nextIdx);

    if (nextIdx >= schema.pages.length || isLastPage) {
      handleSubmit();
    } else {
      setCurrentPageIndex(nextIdx);
    }
  };

  const handleSubmit = () => {
    setSubmitted(true);
    trackEvent(formId, "submit", currentPageIndex);
    localStorage.removeItem(storageKey);
    onSubmit(answers);
  };

  const setFieldAnswer = (fieldId: string, value: any) => {
    setAnswers((a) => ({ ...a, [fieldId]: value }));
  };

  const theme = schema.theme ?? {};
  const radiusClass =
    theme.borderRadius === "sharp"
      ? "rounded-none"
      : theme.borderRadius === "pill"
      ? "rounded-2xl"
      : "rounded-lg";

  if (!page) {
    return <div className="text-sm text-muted-foreground">Form configuration error: no pages found.</div>;
  }

  return (
    <div className="space-y-6">
      {/* Resume banner */}
      {showResumeBanner && (
        <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
          <span>You have a saved response — continue where you left off?</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowResumeBanner(false)}>
              Start over
            </Button>
            <Button size="sm" onClick={resumeAnswers} data-testid="button-resume-form">
              Continue
            </Button>
          </div>
        </div>
      )}

      {/* Logo */}
      {theme.logoUrl && (
        <img src={theme.logoUrl} alt="Logo" className="h-12 object-contain" />
      )}

      {/* Progress indicator */}
      {schema.pages.length > 1 && (
        <div className="flex gap-1">
          {schema.pages.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full ${
                i <= currentPageIndex ? "bg-primary" : "bg-muted"
              }`}
            />
          ))}
        </div>
      )}

      {/* Page title */}
      {page.title && (
        <h2 className="text-xl font-semibold">{page.title}</h2>
      )}

      {/* Fields */}
      <div className="space-y-5">
        {page.fields.map((field) => (
          <FieldRenderer
            key={field.id}
            field={field}
            value={answers[field.id]}
            onChange={(v) => setFieldAnswer(field.id, v)}
            formId={formId}
          />
        ))}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2">
        {currentPageIndex > 0 ? (
          <Button
            variant="outline"
            onClick={() => setCurrentPageIndex((i) => i - 1)}
            data-testid="button-prev-page"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
        ) : (
          <div />
        )}
        <Button
          onClick={handleNext}
          disabled={isSubmitting}
          data-testid={isLastPage ? "button-submit-schema-form" : "button-next-page"}
          style={theme.primaryColor ? { backgroundColor: theme.primaryColor, borderColor: theme.primaryColor } : undefined}
        >
          {isSubmitting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : null}
          {isLastPage ? "Submit" : (
            <>Next <ChevronRight className="w-4 h-4 ml-1" /></>
          )}
        </Button>
      </div>
    </div>
  );
}
